import { useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'
import { Block } from '@/components/base/Block'
import { PrimaryButton } from '@/components/base/Button'
import { buttonSize } from '@/components/base/Button/Base'
import { Heading3 } from '@/components/base/Heading'
import { updateJobPostingMemo } from '@/api/job-posting-service-api'
import { showToast, showErrorToast } from '@/components/SnackBar/BaseSnackBar'
import { useApp } from '@/hooks/useApp'
import useIsMobile from '@/hooks/useMobile'
import { borderLine } from '@/styles/border'
import { fontSize } from '@/styles/font'
import spaceStyle from '@/styles/space'
import styled from 'styled-components'

interface TextareaProps {
  isMobile: boolean
  theme: any
  hasError?: boolean
}

const StyledTextarea = styled.textarea<TextareaProps>`
  /* 2行表示のため高さを2行相当に設定 */
  flex: 1;
  min-width: 0;
  width: auto;
  /* 高さを2行分に固定 */
  height: auto;
  min-height: ${(p: TextareaProps) => (p.isMobile ? '40px' : '44px')};
  line-height: 1.4;

  padding: ${spaceStyle.S}px;
  border: 1px ${borderLine.Solid} #d0d0d0;
  border-radius: 8px;
  font-size: ${(p: TextareaProps) => (p.isMobile ? fontSize.XS : fontSize.S)}px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  max-width: 100%;

  /* エラー時の背景色 */
  background-color: ${(p: TextareaProps) =>
    p.hasError === true
      ? p.theme.errorUltralightColor?.color ?? '#ffebee'
      : 'transparent'};

  /* フォーカス時 */
  &:focus {
    border: 2px ${borderLine.Solid} #333333;
    padding: calc(${spaceStyle.S}px - 1px);
  }

  &::placeholder {
    color: ${(p: TextareaProps) => p.theme.subTextColor.color};
  }
`

// 横幅を制限して中央寄せにするラッパー
const StyledMemoRow = styled.div`
  width: 100%;
  max-width: 800px; /* デスクトップで横に伸びすぎないよう制限 */
  margin: 0 auto; /* 中央寄せ */
`

interface JobPostingMemoInputProps {
  jobPostingId: string
  initialMemo?: string
  onMemoUpdated?: (memo: string) => void
}

const JobPostingMemoInput = ({
  jobPostingId,
  initialMemo = '',
  onMemoUpdated
}: JobPostingMemoInputProps): ReactElement => {
  const app = useApp()
  const isMobile = useIsMobile()
  const [memo, setMemo] = useState(initialMemo)
  const [baselineMemo, setBaselineMemo] = useState(initialMemo) // 保存後サーバ反映済みテキスト
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const maxLength = 100

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue = e.target.value
    setMemo(newValue)
    if (errorMessage !== null) {
      setErrorMessage(null)
    }
  }

  const extractApiErrorMessage = async (
    error: unknown
  ): Promise<string | null> => {
    const isNonEmptyString = (v: unknown): v is string =>
      typeof v === 'string' && v.length > 0
    const isRecord = (v: unknown): v is Record<string, unknown> =>
      typeof v === 'object' && v !== null

    if (isRecord(error)) {
      const e: Record<string, unknown> = error

      if (e.response instanceof Response) {
        console.log('Found Response object, attempting to read body...')
        try {
          // レスポンスボディを読み取り
          const responseClone = e.response.clone()
          const text = await responseClone.text()

          if (text !== '' && text.trim() !== '') {
            try {
              const parsed = JSON.parse(text)
              if (isRecord(parsed) && isNonEmptyString(parsed.message)) {
                return parsed.message
              }
            } catch {
              // JSONでない場合はそのまま返す
              return text
            }
          }
        } catch (readError) {
          console.error('Error reading response body:', readError)
        }
      }
    }
    return null
  }

  const handleSave = async (): Promise<void> => {
    if (isSaving) return

    setErrorMessage(null)
    setIsSaving(true)

    try {
      // 更新APIが正規化後の文字列を返す
      const normalized = await updateJobPostingMemo(jobPostingId, memo)
      setMemo(normalized)
      setBaselineMemo(normalized)
      onMemoUpdated?.(normalized)
      showToast('メモを保存しました')
    } catch (error) {
      const msg = await extractApiErrorMessage(error)
      if (msg !== null && msg !== '') {
        setErrorMessage(msg)
      }
      // エラーが発生した場合は常にトーストも表示
      showErrorToast('メモの保存に失敗しました')
      console.error('Memo save error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const isChanged = memo !== baselineMemo

  return (
    <Block
      margin={{ top: spaceStyle.S }}
      padding={{ horizontal: isMobile ? spaceStyle.XS : spaceStyle.S }}
    >
      {/* 入力と保存ボタンを横並びに配置 */}
      <StyledMemoRow>
        <Block
          display="flex"
          alignItems="center"
          margin={{ bottom: spaceStyle.XS }}
          style={{ paddingLeft: spaceStyle.L }}
        >
          <StyledTextarea
            theme={app.theme}
            isMobile={isMobile}
            hasError={errorMessage !== null && errorMessage !== ''}
            value={memo}
            onChange={handleInputChange}
            placeholder="メモを入力する（最大100文字）"
            maxLength={maxLength}
            rows={2}
          />

          <PrimaryButton
            onClick={() => {
              void handleSave()
            }}
            disabled={isSaving || !isChanged}
            height={buttonSize.M}
            padding={{ horizontal: spaceStyle.M }}
            style={{
              marginLeft: spaceStyle.S,
              flex: '0 0 auto',
              backgroundColor:
                isSaving || !isChanged
                  ? app.theme.disabledColor.color
                  : app.theme.primaryMainColor.color,
              opacity: isSaving || !isChanged ? 0.6 : 1
            }}
          >
            <Heading3
              fixedSPfontSize
              fontSizeSP={isMobile ? fontSize.XXS : fontSize.S}
              textColor={app.theme.whiteTextColor}
              margin={{ all: spaceStyle.NONE }}
            >
              メモ保存
            </Heading3>
          </PrimaryButton>
        </Block>
        {errorMessage !== null && errorMessage !== '' && (
          <div
            style={{
              color: '#d32f2f',
              fontSize: fontSize.XXS,
              lineHeight: 1.4,
              marginTop: 4,
              whiteSpace: 'pre-wrap',
              textAlign: 'left'
            }}
          >
            {errorMessage}
          </div>
        )}
      </StyledMemoRow>
    </Block>
  )
}

export default JobPostingMemoInput
